import {
  Button,
  Dialog,
  DialogContent,
  Snackbar,
  Typography,
} from '@material-ui/core';
import { NetworkIndicator, colors } from 'design-system';
import React, { useCallback, useEffect } from 'react';
import { useClearCache } from 'react-clear-cache';
import { hotjar } from 'react-hotjar';
import { ConnectedProps, connect } from 'react-redux';
import { BrowserRouter as Router, Routes } from 'react-router-dom';

// Data Layer
import { registerDomain } from 'data-layer-cr/dist/authentication';
import { updateLastNetworkErrorMessage } from 'data-layer-cr/dist/network';

// Functions
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import applicationDomain from './functions/applicationDomain';

import './App.css';
import defaultVersionCacheConfig from './constants/defaultVersionCacheConfig';
import routes from './routing/route';
import { RootState } from './store/rootReducer';

const { REACT_APP_HOTJAR_KEY } = process.env;

const growthbook = new GrowthBook({
  apiHost: process.env.REACT_APP_GROWTHBOOK_API_HOST,
  clientKey: process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
});

export const App = ({
  errorMessage,
  updateLastNetworkErrorMessageAction,
}: PropsFromRedux): React.JSX.Element => {
  const { isLatestVersion, emptyCacheStorage } = useClearCache(
    defaultVersionCacheConfig,
  );

  useEffect(() => {
    if (!isLatestVersion) {
      setTimeout(() => {
        emptyCacheStorage();
      }, 3500);
    }
  }, [isLatestVersion, emptyCacheStorage]);

  useEffect(() => {
    hotjar.initialize(Number(REACT_APP_HOTJAR_KEY), 6);
    growthbook.loadFeatures({ autoRefresh: true });
  }, []);

  const handleMessageClose = useCallback(
    (_: React.SyntheticEvent | React.MouseEvent, reason?: string): void => {
      if (reason !== 'clickaway') {
        updateLastNetworkErrorMessageAction('');
      }
    },
    [updateLastNetworkErrorMessageAction],
  );

  const appDomain = applicationDomain(window.location.hostname);
  useEffect(() => {
    registerDomain(appDomain);
  }, [appDomain]);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
        useEnterprise
      >
        <Router>
          <Routes>{routes.map((route) => route)}</Routes>
        </Router>

        {/* Automatic app update */}
        <Dialog data-testid="update-app-modal" open={!isLatestVersion}>
          <DialogContent style={{ width: 295, padding: '31.5px 20px' }}>
            <NetworkIndicator requesting message="Actualizando..." />
          </DialogContent>
        </Dialog>

        {/* The network error message snackbar */}
        <Snackbar
          data-testid="snackbar"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          autoHideDuration={5000}
          open={errorMessage !== '' && isLatestVersion}
          message={
            <Typography variant="caption" style={{ color: colors.white }}>
              {errorMessage}
            </Typography>
          }
          onClose={handleMessageClose}
          action={
            <Button
              data-testid="close-button"
              size="small"
              onClick={handleMessageClose}
              style={{ color: colors.onahau }}
            >
              cerrar
            </Button>
          }
        />
      </GoogleReCaptchaProvider>
    </GrowthBookProvider>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  errorMessage: string;
} => ({
  errorMessage: state.networkStatus.lastNetworkErrorMessage,
});

const reduxActions = {
  updateLastNetworkErrorMessageAction: updateLastNetworkErrorMessage,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(App);
